/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useState } from 'react';
import { useNavigate } from 'react-router';

// Mantine
import {
	ActionIcon,
	Badge,
	Box,
	Flex,
	Group,
	Image,
	Paper,
	Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

// Animation
import { AnimatePresence, motion } from 'framer-motion';

// i18n
import { useTranslation } from 'react-i18next';

// Icons
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import { IconFilter } from '@tabler/icons-react';

// UI Components
import { AdditionalityPoolProjectCard } from '@components/v2/ui/AdditionalityPoolProjectCard/AdditionalityPoolProjectCard';
import { AdditionalityProjectCard } from '@components/v2/ui/AdditionalityProjectCard/AdditionalityProjectCard';
import { Pagination } from '@components/v2/ui/Pagination';
import { ProjectCardSkeleton } from '@components/v2/ui/ProjectCard/ProjectCardSkeleton';
import { Title } from '@components/v2/ui/Title';

// Config
import { NOT_APPLICABLE } from '@config/constants/common';
import { ROLES } from '@config/constants/roles';

// Features
import { useAdditionalityProjects } from '@features/catalogs/additionality-projects/list/api/get-additionality-projects';
import { Filter } from '@features/catalogs/additionality-projects/list/components/Filter';
import { useAdditionalityProject } from '@features/catalogs/additionality-projects/list/hooks/useAdditionalityProjects';
import { useActivateAdditionalityProject } from '@features/resources/additionalities/api/activate-additionality-project';

// Libs
import { useAuthorization } from '@lib/authorization';
import { interpolate } from '@utils/functions';

// Types
import { TAdditionalityProject } from '@global-types/api/api';

// Styles
import { useWhoAmI } from '@api/get-whoami';
import { ACLS } from '@config/constants/acls';
import { EProjectTechnology } from '@global-types/api/enum';
import { checkAclFront } from '@utils/acl-front';
import classes from './additionality-projects.module.css';

/* -------------------------------------------------------------------------- */
/*                                Component                                   */
/* -------------------------------------------------------------------------- */

const AdditionalityProjects = () => {
	/* ------------------------------- HOOKS -------------------------------- */
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { checkRole, role } = useAuthorization();

	const [filtersNbr, setFiltersNbr] = useState(0);
	const [
		openedFilters,
		{ open: openFilters, close: closeFilters },
	] = useDisclosure(false);

	/* ------------------------------- STATES/APIs -------------------------- */
	const {
		handleChangePaginationSize,
		handleChangePagination,
		additionalityProjectsPage,
		additionalityProjectsPageSize,
		additionalityProjectsQueryParams,
	} = useAdditionalityProject();

	const { data: cachedUser, isPending: isPendingWhoAmI } =
		useWhoAmI();

	const producerRole = cachedUser?.roles.find((r) =>
		['PRODUCER_ADMIN', 'PRODUCER_STAFF'].includes(r.role)
	);

	const entityId = producerRole?.entity ?? null;

	const mergedQueryParams = entityId
		? {
				...additionalityProjectsQueryParams,
				entity: entityId,
			}
		: additionalityProjectsQueryParams;

	const {
		data: additionalityProjects,
		total: totalItems,
		totalPages,
		isPending,
	} = useAdditionalityProjects(
		additionalityProjectsPage,
		additionalityProjectsPageSize,
		mergedQueryParams
	);

	const { mutate } = useActivateAdditionalityProject(
		additionalityProjectsPage,
		additionalityProjectsPageSize,
		additionalityProjectsQueryParams,
		''
	);

	/* ------------------------------- RENDER ------------------------------- */
	return (
		<Box>
			{/* ----------------------------- Pool Projects ----------------------------- */}
			<Box
				style={{
					display: 'grid',
					gridTemplateColumns:
						'repeat(auto-fill, minmax(clamp(300px, 50%, 643px), 1fr))',
					gap: 'var(--mantine-spacing-sm)',
				}}
			>
				{Array.isArray(additionalityProjects) &&
					additionalityProjects.length > 0 &&
					additionalityProjects
						.filter(
							(project: TAdditionalityProject) =>
								project?.technology ==
								EProjectTechnology.PROJECT_FOUNDER
						)
						.map(
							(project: TAdditionalityProject, index) => (
								<AnimatePresence key={index} mode="wait">
									<motion.div
										key={'empty'}
										initial={{ y: 10, opacity: 0 }}
										animate={{ y: 0, opacity: 1 }}
										exit={{ y: -10, opacity: 0 }}
										transition={{ duration: 0.2 }}
										style={{
											gridColumn:
												additionalityProjects.filter(
													(
														project: TAdditionalityProject
													) =>
														project?.technology ==
														EProjectTechnology.PROJECT_FOUNDER
												).length %
													2 !==
													0 &&
												index ===
													additionalityProjects.filter(
														(
															project: TAdditionalityProject
														) =>
															project?.technology ==
															EProjectTechnology.PROJECT_FOUNDER
													).length -
														1
													? '1 / -1'
													: 'auto',
											height: '100%',
										}}
									>
										<AdditionalityPoolProjectCard
											title={project.name}
											cover={project.cover}
											description={interpolate(
												t(
													'global.additionalityPoolDescription'
												),
												{
													nom_project: project.name,
													organization:
														project.organization,
												}
											)}
											financing={{
												unlimited:
													project?.financing?.unlimited,
												pledged_euro:
													project?.financing
														?.pledged_euro,
												target_euro:
													project?.financing?.target_euro,
											}}
											onLearnMore={() => {
												navigate(
													`/additionality-projects/${project.id}`
												);
											}}
											fullWidth={true}
											showMonetization={
												!checkRole([ROLES.STAFF])
											}
										/>
									</motion.div>
								</AnimatePresence>
							)
						)}
			</Box>

			{/* ----------------------------- Main Layout ----------------------------- */}
			<Paper mt={'xs'} className={classes['main-layout']}>
				<Flex flex={1} direction={'column'}>
					{/* Filters Modal */}
					{checkAclFront(
						ACLS.FRONT.ADDITIONALITIES.FILTER,
						role as ROLES
					) && (
						<Filter
							opened={openedFilters}
							onClose={closeFilters}
							title={t('filters.titles.projects')}
							onApplyFilters={(filters) => {
								setFiltersNbr(
									Object.keys(filters).length
								);
							}}
							value={additionalityProjectsQueryParams}
						/>
					)}

					{/* Filters Action Bar */}
					<Box className={classes['action-bar']}>
						<Flex
							flex={1}
							justify={'space-between'}
							wrap={'wrap'}
							align={'center'}
							pl={'var(--mantine-spacing-lg)'}
						>
							<Title
								order={6}
								c={'var(--mantine-color-igluPrimary-9)'}
							>
								{t('global.additionalities')}
							</Title>
							<Group gap={'var(--mantine-spacing-xs)'}>
								<div style={{ position: 'relative' }}>
									{filtersNbr > 0 && (
										<Badge
											style={{
												position: 'absolute',
												zIndex: '3',
												right: '-3px',
												fontFamily:
													'InterDisplay-Regular',
											}}
											color="var(--mantine-color-igluRed-9)"
											size="xs"
											circle
										>
											{filtersNbr}
										</Badge>
									)}
									<ActionIcon
										variant="outline"
										size="48px"
										radius="xl"
										color={
											'var(--mantine-color-igluBorder-9)'
										}
										data-testid="bell-icon-button"
										onClick={openFilters}
										bg={
											filtersNbr
												? 'var(--mantine-color-igluPrimary-9)'
												: 'transparent'
										}
									>
										<IconFilter
											color={
												filtersNbr
													? 'var(--mantine-color-igluTextButton-9)'
													: 'var(--mantine-color-igluPrimary-9)'
											}
											radius="xl"
											size={24}
											stroke={1.5}
										/>
									</ActionIcon>
								</div>
							</Group>
						</Flex>
					</Box>

					{/* ----------------------------- Projects List ----------------------------- */}
					<Box
						style={{
							display: 'grid',
							gridTemplateColumns:
								'repeat(auto-fill, minmax(300px, 1fr))',
							gap: 'var(--mantine-spacing-sm)',
						}}
					>
						{isPending
							? [...Array(10).keys()].map(
									(_project, index) => (
										<AnimatePresence
											key={index}
											mode="wait"
										>
											<motion.div
												key={'empty'}
												initial={{ y: 10, opacity: 0 }}
												animate={{ y: 0, opacity: 1 }}
												exit={{ y: -10, opacity: 0 }}
												transition={{ duration: 0.2 }}
												style={{
													display: 'flex',
													flex: 1,
												}}
											>
												<ProjectCardSkeleton
													fullWidth={true}
												/>
											</motion.div>
										</AnimatePresence>
									)
								)
							: additionalityProjects &&
								Array.isArray(additionalityProjects) &&
								additionalityProjects.length > 0 &&
								additionalityProjects
									.filter(
										(project) =>
											project?.technology !==
											EProjectTechnology.PROJECT_FOUNDER
									)
									.map((project) => (
										<AnimatePresence
											key={project.id}
											mode="wait"
										>
											<motion.div
												key={project.id}
												initial={{ y: 10, opacity: 0 }}
												animate={{ y: 0, opacity: 1 }}
												exit={{ y: -10, opacity: 0 }}
												transition={{ duration: 0.2 }}
												style={{
													display: 'flex',
													flex: 1,
												}}
											>
												<AdditionalityProjectCard
													cover={project?.image}
													projectType={project?.type}
													name={project?.name}
													active={project?.active}
													fullWidth={true}
													supportedByLogo={
														project?.supportedBy
													}
													location={{
														countryCode:
															project?.country?.code ||
															NOT_APPLICABLE,
														place:
															project?.location?.place ||
															NOT_APPLICABLE,
														countryName:
															project?.country?.name ||
															NOT_APPLICABLE,
													}}
													onStatusChange={async () => {
														await mutate({
															active: !project.active,
															ids: [project.id],
														});
													}}
													fundedPortion={
														project?.financing
															?.pledged_euro || 0
													}
													onClick={() => {
														navigate(
															`/additionality-projects/${project.id}`
														);
													}}
													showStatus={checkAclFront(
														ACLS.FRONT.ADDITIONALITIES
															.STATUS_TAG,
														role as ROLES
													)}
													readonly={
														!checkRole([
															ROLES.ADMIN,
															ROLES.SUPERADMIN,
														])
													}
													showPrice={
														!checkRole([ROLES.STAFF])
													}
												/>
											</motion.div>
										</AnimatePresence>
									))}
					</Box>

					{/* ----------------------------- Empty List ----------------------------- */}
					{additionalityProjects &&
						Array.isArray(additionalityProjects) &&
						additionalityProjects.filter(
							(project) =>
								project?.technology !==
								EProjectTechnology.PROJECT_FOUNDER
						).length === 0 &&
						!isPending && (
							<Box className={classes.containerEmptyList}>
								<Box
									className={
										classes.containerTextsNoRecords
									}
								>
									<Image
										src={NoRecordsIcon}
										className={classes.iconNoRecords}
									/>
									<>
										<Text
											className={classes.titleNoRecords}
											variant="subtitle1"
											c="var(--mantine-color-igluPrimary-9)"
										>
											{t(
												'global.datatable.noResultsFound'
											)}
										</Text>
									</>
								</Box>
							</Box>
						)}
				</Flex>

				{/* ----------------------------- Pagination ----------------------------- */}
				<Box className={classes['pagination-bar']}>
					<Pagination
						pageSize={additionalityProjectsPageSize}
						totalItems={totalItems}
						activePage={additionalityProjectsPage}
						onPageSizeChange={handleChangePaginationSize}
						onChange={handleChangePagination}
						total={totalPages ? totalPages : 0}
					/>
				</Box>
			</Paper>
		</Box>
	);
};

export default AdditionalityProjects;
